import {
  ChangeEvent,
  MouseEvent,
  KeyboardEvent,
  useState,
  forwardRef,
  useEffect,
} from "react";
import cn from "classnames";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import LoadingButton from "@ebsco-ui/loading-button";
import { NativeDropdownRef } from "./type";
import { NativeDropdownProps } from "./interface";
import NativeDropdownOptions from "./native-dropdown-options";

import "./native-dropdown.scss";

const NativeDropdown = forwardRef<NativeDropdownRef, NativeDropdownProps>(
  (
    {
      items,
      id,
      label: { value: labelValue, display: displayLabel = true },
      onChange,
      initialSelectedValue = "",
      placeholder,
      className: consumerClassName,
      styleType = "primary",
      onOpen,
      disabled = false,
      isLoading = false,
      loadingPlaceholder,
      announceLoadingStart,
      announceLoadingEnd,
    },
    ref,
  ) => {
    const [renderLoadingComponent, setRenderLoadingComponent] = useState(false);
    const [selected, setSelected] = useState(initialSelectedValue);
    const getBlocksWith = createBemBlockBuilder([
      "eb-native-dropdown",
      consumerClassName,
    ]);

    const filteredItems = items.filter((item) => !item.isDivider);
    const selectedLabel = filteredItems
      .find(({ value }) => value === selected)
      ?.label.toString();

    useEffect(() => {
      /* We don't want the loading component to be rendered
       * if the dropdown is not supposed to have a loading state.*/

      if (isLoading && !renderLoadingComponent) {
        setRenderLoadingComponent(true);
      }
    }, [isLoading, renderLoadingComponent]);

    const handleChange = ({
      target: { value: selectedValue },
    }: ChangeEvent<HTMLSelectElement>) => {
      const selectedOption = filteredItems.find(
        ({ value }) => value === selectedValue,
      );

      if (!selectedOption) {
        return;
      }

      onChange && onChange(selectedOption);
      setSelected(selectedValue);
    };

    const handleOnOpen = (
      event: KeyboardEvent & MouseEvent<HTMLSelectElement>,
    ) => {
      if (!onOpen) {
        return;
      }

      if (!event.key) {
        onOpen(event);
        return;
      }

      switch (event.key) {
        case "Spacebar": // ie11 support
        case " ":
        case "Enter":
        case "ArrowUp":
        case "ArrowDown":
          onOpen(event);
      }
    };

    return (
      <div
        className={cn(
          getBlocksWith(),
          getBlocksWith(`--${styleType}`),
          disabled && getBlocksWith("--disabled"),
        )}
      >
        <label
          className={cn(
            getBlocksWith("__label"),
            getBlocksWith(`__label--${styleType}`),
            !displayLabel && getBlocksWith("__label--hidden"),
          )}
          htmlFor={id}
        >
          {labelValue}
        </label>
        <div
          className={cn(
            getBlocksWith("__menu-wrapper"),
            getBlocksWith(`__menu-wrapper--${styleType}`),
          )}
          data-auto="eb-menu-wrapper"
        >
          {renderLoadingComponent && (
            <div
              className={cn(
                getBlocksWith("__loader"),
                isLoading && getBlocksWith("__loader--loading"),
              )}
            >
              <LoadingButton
                tabIndex={-1}
                className={getBlocksWith("__loader-button")}
                styleType={styleType}
                loadingPlaceholder={
                  loadingPlaceholder || selectedLabel || placeholder
                }
                announceLoadingStart={announceLoadingStart}
                announceLoadingEnd={announceLoadingEnd}
                isLoading={isLoading}
                disabled={disabled}
                children={loadingPlaceholder || selectedLabel || placeholder}
              />
            </div>
          )}
          <select
            ref={ref}
            className={cn(
              getBlocksWith("__menu"),
              getBlocksWith(`__menu--${styleType}`),
              isLoading && getBlocksWith("__menu--loading"),
            )}
            id={id}
            value={selected}
            onChange={handleChange}
            onClick={handleOnOpen}
            onKeyDown={handleOnOpen}
            data-auto="eb-native-dropdown"
            disabled={isLoading || disabled}
          >
            <NativeDropdownOptions
              items={filteredItems}
              placeholder={placeholder}
              className={cn(
                getBlocksWith("__option"),
                getBlocksWith(`__option--${styleType}`),
              )}
            />
          </select>
        </div>
      </div>
    );
  },
);

NativeDropdown.displayName = "NativeDropdown";

export default NativeDropdown;
